import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import IconBadge from "./IconBadge";
import "./WhyChooseCarousel.css";

/* ============================================================================
   The "Why Choose MBA Solutions" strip, shared by every page that asks the
   question — Home, Services, and each service detail page.

   It began on the Home page and was lifted here on the user's instruction
   (2026-09-14) so all of them render the identical structure and can no longer
   drift apart. Home renders through this component too.

   The strip is a NATIVE scroll container with scroll snapping (see the CSS).
   This component only moves it by one card when an arrow is pressed and keeps
   the arrows' disabled state in step with where the strip has been scrolled —
   by finger, trackpad or keyboard just as much as by the buttons. The pitch is
   MEASURED off the first card rather than declared, so the breakpoint that
   changes card width needs no matching change here.
   ========================================================================= */

/** The five illustrations in public/assets/why-choose, in their Home order. */
export const WHY_CHOOSE_IMAGES = [
  "/assets/why-choose/proven-experience.webp",
  "/assets/why-choose/industry-knowledge.webp",
  "/assets/why-choose/complete-certification-support.webp",
  "/assets/why-choose/global-standards-expertise.webp",
  "/assets/why-choose/long-term-business-improvement.webp",
];

/**
 * Turn a page's plain list of reasons into cards for this strip.
 *
 * The approved documents give most pages their reasons as bare lines with no
 * artwork of their own, so they borrow the shared set positionally. Any list
 * longer than the set simply cycles it rather than leaving a card blank.
 */
export const toWhyChooseReasons = (lines) =>
  lines.map((line, i) => ({
    title: line,
    image: WHY_CHOOSE_IMAGES[i % WHY_CHOOSE_IMAGES.length],
  }));

/**
 * @param {string} id        heading id, for the section's aria-labelledby
 * @param {string} title     section heading
 * @param {string} [subtitle] optional line under the heading
 * @param {array}  reasons   `{ title, description?, image, badgeIcon? }` —
 *                           a `badgeIcon` (react-icons component) is drawn as
 *                           an IconBadge in place of the illustration; only
 *                           the Home page passes one

 * @param {string} [tone]    "light" (default, white) or "muted" (gray-50)
 */
const WhyChooseCarousel = ({ id, title, subtitle, reasons, tone = "light" }) => {
  const viewportRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const sync = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // 1px of slack: fractional layout widths mean scrollLeft rarely lands
    // exactly on 0 or on max
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft >= max - 1);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    let raf = 0;
    const onScroll = () => {
      if (!raf) {
        raf = window.requestAnimationFrame(() => {
          raf = 0;
          sync();
        });
      }
    };

    sync();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [sync]);

  const step = (direction) => {
    const el = viewportRef.current;
    if (!el) return;
    const card = el.firstElementChild;
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    const pitch = card ? card.offsetWidth + gap : el.clientWidth * 0.8;
    el.scrollBy({ left: direction * pitch, behavior: "smooth" });
  };

  return (
    <section
      className={`why-choose ${
        tone === "muted" ? "bg-gray-50" : "bg-white"
      } py-14 lg:py-20`}
      aria-labelledby={id}
    >
      <div className="brand-col">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="why-choose__head"
        >
          <div>
            <h2
              id={id}
              className="text-3xl md:text-4xl font-extrabold tracking-tight text-[var(--brand-primary)] drop-shadow-sm"
            >
              {title}
            </h2>
            {subtitle && (
              <h3 className="mt-4 text-lg md:text-xl font-bold text-gray-900">
                {subtitle}
              </h3>
            )}
          </div>

          <div className="why-choose__nav">
            <button
              type="button"
              className="why-choose__navbtn"
              aria-label="Previous reasons"
              disabled={atStart}
              onClick={() => step(-1)}
            >
              <FaArrowLeft aria-hidden="true" />
            </button>
            <button
              type="button"
              className="why-choose__navbtn"
              aria-label="More reasons"
              disabled={atEnd}
              onClick={() => step(1)}
            >
              <FaArrowRight aria-hidden="true" />
            </button>
          </div>
        </motion.div>

        {/* focusable and labelled: a scrollable region has to be reachable by
            keyboard, and the arrow keys then pan it */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
          className="why-choose__viewport"
          ref={viewportRef}
          role="group"
          aria-label={title}
          tabIndex={0}
        >
          {reasons.map((reason) => (
            <article
              key={reason.title}
              className="brand-card why-choose__card items-center"
            >
              <span className="why-choose__art">
                {reason.badgeIcon ? (
                  <IconBadge icon={reason.badgeIcon} size="lg" />
                ) : (
                  <img
                    className="why-choose__img"
                    src={reason.image}
                    alt=""
                    loading="lazy"
                  />
                )}
              </span>
              <h4 className="mt-4 text-lg font-bold text-gray-900">
                {reason.title}
              </h4>
              {reason.description && (
                <p className="mt-3 text-sm md:text-base leading-relaxed text-gray-600">
                  {reason.description}
                </p>
              )}
            </article>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseCarousel;
